/**
 * Servicio para revisión y activación de solicitudes de productos y campañas.
 * - Admin: fetch todas, aprobar (crear recurso + actualizar solicitud con recursoId/accesoUrl), rechazar.
 * - Empresa: fetch por empresaId (usar en dashboard).
 * Colecciones: solicitudes_producto, solicitudes_campana.
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import * as CompanyLimitsService from '../../cliente/services/companyLimitsService';

const COL_PRODUCTO = 'solicitudes_producto';
const COL_CAMPANA = 'solicitudes_campana';

function getOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return '';
}

/** Admin: listar solicitudes de productos (filtro: todos | pendiente | en_revision | aprobada | rechazada | activa) */
export async function fetchSolicitudesProductoAdmin(filter = 'todos') {
  const col = collection(db, COL_PRODUCTO);
  try {
    let q;
    if (filter === 'todos') {
      q = query(col, orderBy('fechaSolicitud', 'desc'), limit(100));
    } else {
      q = query(col, where('estado', '==', filter), orderBy('fechaSolicitud', 'desc'), limit(100));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    const snap = await getDocs(col);
    let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (filter !== 'todos') list = list.filter((x) => x.estado === filter);
    list.sort((a, b) => {
      const ta = a.fechaSolicitud?.toMillis?.() ?? a.fechaSolicitud?.toDate?.()?.getTime?.() ?? 0;
      const tb = b.fechaSolicitud?.toMillis?.() ?? b.fechaSolicitud?.toDate?.()?.getTime?.() ?? 0;
      return tb - ta;
    });
    return list.slice(0, 100);
  }
}

/** Admin: listar solicitudes de campañas */
export async function fetchSolicitudesCampanaAdmin(filter = 'todos') {
  const col = collection(db, COL_CAMPANA);
  try {
    let q;
    if (filter === 'todos') {
      q = query(col, orderBy('fechaSolicitud', 'desc'), limit(100));
    } else {
      q = query(col, where('estado', '==', filter), orderBy('fechaSolicitud', 'desc'), limit(100));
    }
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    const snap = await getDocs(col);
    let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (filter !== 'todos') list = list.filter((x) => x.estado === filter);
    list.sort((a, b) => {
      const ta = a.fechaSolicitud?.toMillis?.() ?? a.fechaSolicitud?.toDate?.()?.getTime?.() ?? 0;
      const tb = b.fechaSolicitud?.toMillis?.() ?? b.fechaSolicitud?.toDate?.()?.getTime?.() ?? 0;
      return tb - ta;
    });
    return list.slice(0, 100);
  }
}

/** Empresa: listar sus solicitudes de productos */
export async function fetchSolicitudesProductoEmpresa(empresaId) {
  if (!empresaId) return [];
  const col = collection(db, COL_PRODUCTO);
  try {
    const q = query(
      col,
      where('empresaId', '==', empresaId),
      orderBy('fechaSolicitud', 'desc'),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    const q = query(col, where('empresaId', '==', empresaId), limit(50));
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => {
      const fa = a.fechaSolicitud?.toDate?.() || new Date(a.fechaSolicitud || 0);
      const fb = b.fechaSolicitud?.toDate?.() || new Date(b.fechaSolicitud || 0);
      return fb - fa;
    });
    return data;
  }
}

/** Empresa: listar sus solicitudes de campañas */
export async function fetchSolicitudesCampanaEmpresa(empresaId) {
  if (!empresaId) return [];
  const col = collection(db, COL_CAMPANA);
  try {
    const q = query(
      col,
      where('empresaId', '==', empresaId),
      orderBy('fechaSolicitud', 'desc'),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    const q = query(col, where('empresaId', '==', empresaId), limit(50));
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => {
      const fa = a.fechaSolicitud?.toDate?.() || new Date(a.fechaSolicitud || 0);
      const fb = b.fechaSolicitud?.toDate?.() || new Date(b.fechaSolicitud || 0);
      return fb - fa;
    });
    return data;
  }
}

/** Construir URL de acceso al recurso publicado en perfil público */
export function buildAccesoUrl(empresaId, tipo) {
  const origin = getOrigin();
  const tab = tipo === 'producto' ? 'productos' : 'campanas';
  return `${origin}/empresa/${empresaId}?tab=${tab}`;
}

/** Admin: aprobar/activar solicitud de producto. Crea producto y actualiza solicitud via Cloud Function. */
export async function aprobarSolicitudProducto(solicitudId, comentarioAdmin, _aprobadoPorUid, solicitudData = null) {
  let solicitud = solicitudData;
  if (!solicitud) {
    const snap = await getDoc(doc(db, COL_PRODUCTO, solicitudId));
    if (!snap.exists()) throw new Error('Solicitud no encontrada');
    solicitud = { id: snap.id, ...snap.data() };
  }
  const empresaId = solicitud.empresaId;
  if (!empresaId) throw new Error('Solicitud sin empresaId');

  const payload = {
    nombre: solicitud.nombre || solicitud.nombre_producto || 'Producto',
    descripcion: solicitud.descripcion || '',
    precio: solicitud.precio ?? 0,
    categoria: solicitud.categoria || '',
    marca: solicitud.marca || '',
    stock: solicitud.stock ?? 0,
    url: solicitud.url || null,
    imagenes: solicitud.imagenes || [],
    visible: true,
    estado: 'activo',
    solicitudOrigenId: solicitudId,
  };
  const accesoUrl = buildAccesoUrl(empresaId, 'producto');
  // Cloud Function crea el producto Y actualiza la solicitud (evita write directo con reglas de admin)
  const result = await CompanyLimitsService.createProduct(empresaId, payload, {
    solicitudId,
    comentarioAdmin: comentarioAdmin || null,
    accesoUrl,
  });

  return { success: true, id: result?.id, accesoUrl };
}

/**
 * Admin: actualiza campos de la solicitud de producto (pendiente / en_revision).
 */
export async function actualizarCamposSolicitudProductoAdmin(solicitudId, patch, _adminUid) {
  // CF valida estado server-side; no se necesita getDoc desde el cliente
  await CompanyLimitsService.adminSolicitudOps({
    accion: 'editar',
    coleccion: 'producto',
    solicitudId,
    patch,
  });
  return { success: true };
}

/** Admin: rechazar solicitud de producto */
export async function rechazarSolicitudProducto(solicitudId, rechazoMotivo, comentarioAdmin) {
  await CompanyLimitsService.adminSolicitudOps({
    accion: 'rechazar',
    coleccion: 'producto',
    solicitudId,
    rechazoMotivo: rechazoMotivo || null,
    comentarioAdmin: comentarioAdmin || null,
  });
  return { success: true };
}

/** Admin: aprobar/activar solicitud de campaña. Crea campaña y actualiza solicitud via Cloud Function. */
export async function aprobarSolicitudCampana(solicitudId, comentarioAdmin, _aprobadoPorUid, solicitudData = null) {
  let solicitud = solicitudData;
  if (!solicitud) {
    const snap = await getDoc(doc(db, COL_CAMPANA, solicitudId));
    if (!snap.exists()) throw new Error('Solicitud no encontrada');
    solicitud = { id: snap.id, ...snap.data() };
  }
  const empresaId = solicitud.empresaId;
  if (!empresaId) throw new Error('Solicitud sin empresaId');

  const fechaInicio = solicitud.fechaInicio
    ? typeof solicitud.fechaInicio === 'string'
      ? new Date(solicitud.fechaInicio)
      : solicitud.fechaInicio?.toDate?.() || new Date(solicitud.fechaInicio)
    : new Date();
  const fechaFin = solicitud.fechaFin
    ? typeof solicitud.fechaFin === 'string'
      ? new Date(solicitud.fechaFin)
      : solicitud.fechaFin?.toDate?.() || new Date(solicitud.fechaFin)
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const imagenesCampana = (() => {
    if (Array.isArray(solicitud.imagenes) && solicitud.imagenes.length > 0) {
      return solicitud.imagenes.filter((u) => typeof u === 'string' && u.length > 0);
    }
    if (solicitud.imagenUrl && typeof solicitud.imagenUrl === 'string') {
      return [solicitud.imagenUrl];
    }
    return [];
  })();

  const payload = {
    titulo: solicitud.titulo || solicitud.nombre || solicitud.nombre_campana || 'Campaña',
    descripcion: solicitud.descripcion || '',
    fechaInicio,
    fechaFin,
    presupuesto: solicitud.presupuesto || 0,
    tipo: solicitud.tipo || 'promocional',
    estado: 'activa',
    visible: true,
    solicitudOrigenId: solicitudId,
    imagenes: imagenesCampana,
    imagenUrl: imagenesCampana[0] || solicitud.imagenUrl || null,
  };
  const accesoUrl = buildAccesoUrl(empresaId, 'campana');
  // Cloud Function crea la campaña Y actualiza la solicitud (evita write directo con reglas de admin)
  const result = await CompanyLimitsService.createCampaign(empresaId, payload, {
    solicitudId,
    comentarioAdmin: comentarioAdmin || null,
    accesoUrl,
  });

  return { success: true, id: result?.id, accesoUrl };
}

/**
 * Admin: guarda comentario visible para la empresa y deja la solicitud en `en_revision`
 * hasta que se active o rechace. Solo aplica si el estado actual es pendiente o en_revision.
 */
/**
 * Admin: actualiza campos de la solicitud (pendiente / en_revision) antes de activar o como corrección.
 * No cambia el estado salvo que combines con guardarComentarioEmpresaCampanaEnRevision.
 */
export async function actualizarCamposSolicitudCampanaAdmin(solicitudId, patch, _adminUid) {
  // CF valida estado server-side; no se necesita getDoc desde el cliente
  await CompanyLimitsService.adminSolicitudOps({
    accion: 'editar',
    coleccion: 'campana',
    solicitudId,
    patch,
  });
  return { success: true };
}

export async function guardarComentarioEmpresaCampanaEnRevision(solicitudId, textoComentario, _adminUid) {
  const texto = String(textoComentario || '').trim();
  if (!texto) throw new Error('Escribe un comentario para la empresa.');
  await CompanyLimitsService.adminSolicitudOps({
    accion: 'en_revision',
    coleccion: 'campana',
    solicitudId,
    texto,
  });
  return { success: true };
}

/** Admin: rechazar solicitud de campaña */
export async function rechazarSolicitudCampana(solicitudId, rechazoMotivo, comentarioAdmin) {
  await CompanyLimitsService.adminSolicitudOps({
    accion: 'rechazar',
    coleccion: 'campana',
    solicitudId,
    rechazoMotivo: rechazoMotivo || null,
    comentarioAdmin: comentarioAdmin || null,
  });
  return { success: true };
}
